import classNames from 'classnames';
import { Formik } from 'formik';
import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import {
  CalculatePaymentPlanRequest,
  CalculatePaymentPlanResponse,
  GetPaymentPlanConfigsV2ResponseType,
  PaymentsAndBillingInfoType,
} from '@clearcover/core-app-types';
import {
  FormInput,
  FormModalContent,
  FormModalHeader,
  Loading,
} from 'Shared/components';

import { FlexGrid, FlexGridColumn } from '@clearcover/atlas';
import { PolicyContext, usePolicyParams } from 'Policies/utils';
import { useToast } from 'Shared/components';
import { useApi, useLazyApi } from 'Shared/services/Api';
import { CustomFormModalFooter } from './CustomFormModalFooter';
import { PaymentScheduleList } from './PaymentScheduleList';
import styles from './styles.module.css';
import {
  dayOfWeekOptions,
  generateMonthlyOptions,
  generateSemiMonthlyOptions,
} from './utils';
import { FeeStrategyPicker } from './FeeStrategyPicker';
import { CanceledError } from 'axios';

const FORM_VALIDATION = Yup.object({
  installmentInterval: Yup.string().required('Payment frequency is required'),
  daysOfWeek: Yup.string().when('installmentInterval', {
    is: (val: string) => val === 'WEEKLY' || val === 'BI_WEEKLY',
    then: (schema: Yup.StringSchema) =>
      schema.required('Day of week is required'),
    otherwise: (schema: Yup.StringSchema) => schema.notRequired(),
  }),
  daysOfMonth: Yup.string().when('installmentInterval', {
    is: (val: string) => val === 'SEMI_MONTHLY' || val === 'MONTHLY',
    then: (schema: Yup.StringSchema) =>
      schema.required('Days of month is required'),
    otherwise: (schema: Yup.StringSchema) => schema.notRequired(),
  }),
});

const FREQUENCIES = [
  {
    key: 'WEEKLY',
    label: 'Weekly',
    description: 'Payments made weekly',
  },
  {
    key: 'BI_WEEKLY',
    label: 'Every two weeks',
    description: 'Payments made every two weeks',
  },
  {
    key: 'SEMI_MONTHLY',
    label: 'Twice a month',
    description: 'Payments made twice a month',
  },
  {
    key: 'MONTHLY',
    label: 'Monthly Payments',
    description: 'Payments made monthly',
  },
  {
    key: 'FULL_PAY',
    label: 'Full pay',
    description: 'Pay entire policy balance today',
  },
];

export type EditPayPlanProps = {
  billingInfo?: PaymentsAndBillingInfoType;
  isRenewalDraft: boolean;
  close: () => void;
};

export const EditPayPlan: FC<EditPayPlanProps> = ({
  billingInfo,
  isRenewalDraft,
  close,
}) => {
  const { toast } = useToast();
  const { policyId, draftId } = usePolicyParams();
  const {
    details: { configDiscoveryParams },
  } = useContext(PolicyContext);

  const history = useHistory();
  const [
    paymentPlanResponse,
    setPaymentPlanResponse,
  ] = useState<CalculatePaymentPlanResponse | null>(null);
  const isRenewalPlanChange = isRenewalDraft && !!draftId;

  const [{ data: planOptions, loading: isLoadingPlanOptions }] = useApi<
    GetPaymentPlanConfigsV2ResponseType
  >(`payPlansV2/configs?configDiscoveryParams=${configDiscoveryParams}`);

  const [selectedInterval, setSelectedInterval] = useState<string>(
    billingInfo?.installmentInterval || ''
  );

  const availableFeeStrategies = useMemo(() => {
    if (!planOptions) return [];

    return (
      Object.values(planOptions.plans).find(
        plan => plan.installmentInterval === selectedInterval
      )?.feeStrategies || []
    );
  }, [planOptions, selectedInterval]);

  const availableFrequencies = useMemo(() => {
    if (!planOptions) return [];

    return FREQUENCIES.filter(freq =>
      Object.values(planOptions.plans)
        .map(plan => plan.installmentInterval)
        .includes(freq.key)
    );
  }, [planOptions]);

  const [{ loading: isCalculatingPayPlan }, calculatePaymentPlan] = useLazyApi({
    url: isRenewalPlanChange
      ? `policies/renewal/${policyId}/calculatePayPlan`
      : 'policies/calculatePayPlan',
    method: 'POST',
  });

  const calculatePayPlan = useCallback(
    ({
      installmentInterval,
      daysOfWeek,
      daysOfMonth,
      feeStrategy,
    }: {
      installmentInterval: string;
      daysOfWeek?: string;
      daysOfMonth?: string;
      feeStrategy?: string;
    }) => {
      const data: CalculatePaymentPlanRequest = {
        policyId,
        installmentInterval,
        feeStrategy,
      };

      if (
        installmentInterval === 'WEEKLY' ||
        installmentInterval === 'BI_WEEKLY'
      ) {
        data.daysOfWeek = [daysOfWeek];
      } else if (installmentInterval === 'SEMI_MONTHLY') {
        data.daysOfMonth = daysOfMonth?.split(',').map(Number);
      } else if (installmentInterval === 'MONTHLY') {
        data.daysOfMonth = [parseInt(daysOfMonth || '1')];
      }

      if (isRenewalPlanChange) {
        data.minimumRequiredDownpayment = {
          amount: 1,
          type: 'INSTALLMENT',
        };
      }

      calculatePaymentPlan({
        data,
      })
        .then(response => {
          setPaymentPlanResponse(response.data as CalculatePaymentPlanResponse);
        })
        .catch(e => {
          setPaymentPlanResponse(null);
          if (!(e instanceof CanceledError)) {
            toast({
              message: 'Error calculating payment plan',
              type: 'error',
              position: 'bottom',
              withSecondaryNav: false,
            });
          }
        });
    },
    [calculatePaymentPlan, isRenewalPlanChange, policyId, toast]
  );

  const createHandleContinue = useCallback(
    ({
      installmentInterval,
      daysOfWeek,
      daysOfMonth,
      feeStrategy,
    }: {
      installmentInterval: CalculatePaymentPlanResponse['installmentInterval'];
      daysOfWeek?: CalculatePaymentPlanResponse['paymentsDueOn']['daysOfWeek'];
      daysOfMonth?: CalculatePaymentPlanResponse['paymentsDueOn']['daysOfMonth'];
      feeStrategy?: string;
    }) => {
      return () => {
        const queryParams = new URLSearchParams({
          installmentInterval,
          ...(daysOfWeek && { daysOfWeek }),
          ...(daysOfMonth && { daysOfMonth }),
          ...(feeStrategy && { feeStrategy }),
        });

        if (isRenewalPlanChange) {
          history.push(
            `/policies/${policyId}/draft/${draftId}/payPlan/summary?${queryParams.toString()}`
          );
        } else {
          history.push(
            `/policies/${policyId}/payPlan/summary?${queryParams.toString()}`
          );
        }
      };
    },
    [draftId, history, isRenewalPlanChange, policyId]
  );

  return (
    <Formik
      initialValues={{
        installmentInterval: billingInfo?.installmentInterval || '',
        daysOfWeek: billingInfo?.paymentsDueOn?.daysOfWeek[0] || '',
        daysOfMonth:
          billingInfo?.paymentsDueOn?.daysOfMonth[0]?.toString() || '',
        selectedStrategy: billingInfo?.feeStrategy || 'predictable',
      }}
      onSubmit={(_, { setSubmitting }) => {
        setSubmitting(false);
      }}
      validationSchema={FORM_VALIDATION}
      validateOnMount
      validateOnChange
    >
      {({
        handleBlur,
        setFieldValue,
        setFieldTouched,
        values,
        touched,
        errors,
      }) => {
        const handleInstallmentChange = (interval: string) => {
          setFieldValue('daysOfWeek', '');
          setFieldValue('daysOfMonth', '');
          setFieldTouched('daysOfWeek', false);
          setFieldTouched('daysOfMonth', false);
          setFieldValue('installmentInterval', interval, true);

          setSelectedInterval(interval);

          // For FULL_PAY, call API immediately since no day selection needed
          if (interval === 'FULL_PAY') {
            calculatePayPlan({
              installmentInterval: interval,
              feeStrategy: values.selectedStrategy,
            });
          }
        };

        const handleDayChange = (field: string, value: string) => {
          setFieldValue(field, value);

          // After setting day value, check if form is complete and call API
          const updatedValues = { ...values, [field]: value };
          if (updatedValues.installmentInterval === 'FULL_PAY') return;

          if (
            ['WEEKLY', 'BI_WEEKLY'].includes(
              updatedValues.installmentInterval
            ) &&
            field === 'daysOfWeek' &&
            value
          ) {
            calculatePayPlan({
              installmentInterval: updatedValues.installmentInterval,
              daysOfWeek: value,
              feeStrategy: updatedValues.selectedStrategy,
            });
          } else if (
            ['SEMI_MONTHLY', 'MONTHLY'].includes(
              updatedValues.installmentInterval
            ) &&
            field === 'daysOfMonth' &&
            value
          ) {
            calculatePayPlan({
              installmentInterval: updatedValues.installmentInterval,
              daysOfMonth: value,
              feeStrategy: updatedValues.selectedStrategy,
            });
          }
        };

        const handleStrategyChange = (strategy: string) => {
          setFieldValue('selectedStrategy', strategy);

          // Recalculate pay plan with new strategy
          calculatePayPlan({
            installmentInterval: values.installmentInterval,
            daysOfWeek:
              values.installmentInterval === 'WEEKLY' ||
              values.installmentInterval === 'BI_WEEKLY'
                ? values.daysOfWeek
                : undefined,
            daysOfMonth:
              values.installmentInterval === 'SEMI_MONTHLY' ||
              values.installmentInterval === 'MONTHLY'
                ? values.daysOfMonth
                : undefined,
            feeStrategy: strategy,
          });
        };

        const isFormIncomplete = () => {
          return Boolean(
            !!values.installmentInterval &&
              values.installmentInterval !== 'FULL_PAY' &&
              ((['WEEKLY', 'BI_WEEKLY'].includes(values.installmentInterval) &&
                (errors.daysOfWeek || !values.daysOfWeek)) ||
                (['SEMI_MONTHLY', 'MONTHLY'].includes(
                  values.installmentInterval
                ) &&
                  (errors.daysOfMonth || !values.daysOfMonth)))
          );
        };

        const shouldShowSchedule =
          !isFormIncomplete() && !isCalculatingPayPlan && paymentPlanResponse;

        const shouldShowFeeStrategyPicker = availableFeeStrategies.length > 0;

        return (
          <>
            <FormModalHeader>Edit Pay Plan</FormModalHeader>
            <FormModalContent>
              <FlexGrid>
                <FlexGridColumn mobile={8} mobileOffset={2}>
                  <section>
                    <h2>Choose a payment plan</h2>
                    <p>
                      Completing the customer's plan change may require a
                      payment today. You'll see the full amount before
                      submitting.
                    </p>
                    <h3>Payment Frequency</h3>

                    {isLoadingPlanOptions ? (
                      <Loading />
                    ) : (
                      <>
                        {availableFrequencies.map(
                          ({ key, label, description }) => {
                            return (
                              <FormInput
                                className={classNames(
                                  styles.costRadioButton,
                                  styles.top,
                                  {
                                    [styles.checked]:
                                      values.installmentInterval === key,
                                  }
                                )}
                                variant={'radio'}
                                label={
                                  <div className={styles.costRadioButtonLabel}>
                                    <h5 className={styles.radioLabel}>
                                      {label}
                                    </h5>
                                    <div className={styles.radioSubLabel}>
                                      {description}
                                    </div>
                                    <div
                                      className={styles.radioButtonCost}
                                    ></div>
                                  </div>
                                }
                                disabled={isCalculatingPayPlan}
                                checked={values.installmentInterval === key}
                                onChecked={() => {
                                  handleInstallmentChange(key);
                                }}
                                name={'installmentInterval'}
                                value={key}
                              />
                            );
                          }
                        )}
                        {values.installmentInterval !== 'FULL_PAY' && (
                          <div className={styles.daySelectionSection}>
                            <div className={styles.daySelectionContainer}>
                              <h3>Payments will be made on</h3>
                              <div className={styles.daySelectionDropdown}>
                                {(values.installmentInterval === 'WEEKLY' ||
                                  values.installmentInterval ===
                                    'BI_WEEKLY') && (
                                  <FormInput
                                    key={`daysOfWeek-${values.installmentInterval}`}
                                    variant="selectAutocomplete"
                                    name="daysOfWeek"
                                    onChange={(value: string) => {
                                      handleDayChange('daysOfWeek', value);
                                    }}
                                    onBlur={handleBlur}
                                    value={values.daysOfWeek}
                                    options={dayOfWeekOptions}
                                    placeholder="Select a payment day"
                                    disabled={isCalculatingPayPlan}
                                    errorMessage={
                                      touched.daysOfWeek
                                        ? errors.daysOfWeek
                                        : undefined
                                    }
                                  />
                                )}
                                {values.installmentInterval ===
                                  'SEMI_MONTHLY' && (
                                  <FormInput
                                    key={`daysOfMonth-${values.installmentInterval}`}
                                    variant="selectAutocomplete"
                                    name="daysOfMonth"
                                    onChange={(value: string) => {
                                      handleDayChange('daysOfMonth', value);
                                    }}
                                    onBlur={handleBlur}
                                    value={values.daysOfMonth?.toString() || ''}
                                    options={generateSemiMonthlyOptions()}
                                    placeholder="Select a payment day"
                                    disabled={isCalculatingPayPlan}
                                    errorMessage={
                                      touched.daysOfMonth
                                        ? errors.daysOfMonth
                                        : undefined
                                    }
                                  />
                                )}
                                {values.installmentInterval === 'MONTHLY' && (
                                  <FormInput
                                    key={`daysOfMonth-${values.installmentInterval}`}
                                    variant="selectAutocomplete"
                                    name="daysOfMonth"
                                    onChange={(value: string) => {
                                      handleDayChange('daysOfMonth', value);
                                    }}
                                    value={values.daysOfMonth?.toString() || ''}
                                    onBlur={handleBlur}
                                    options={generateMonthlyOptions()}
                                    placeholder="Select a payment day"
                                    disabled={isCalculatingPayPlan}
                                    errorMessage={
                                      touched.daysOfMonth
                                        ? errors.daysOfMonth
                                        : undefined
                                    }
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {shouldShowFeeStrategyPicker && (
                          <FeeStrategyPicker
                            feeStrategies={availableFeeStrategies}
                            selectedStrategy={values.selectedStrategy}
                            onStrategySelect={(strategy: string) =>
                              handleStrategyChange(strategy)
                            }
                            disabled={!isRenewalDraft}
                          />
                        )}

                        {shouldShowSchedule && (
                          <PaymentScheduleList
                            paymentPlan={paymentPlanResponse}
                          />
                        )}
                      </>
                    )}
                  </section>
                </FlexGridColumn>
              </FlexGrid>
            </FormModalContent>
            <CustomFormModalFooter
              paymentPlan={paymentPlanResponse}
              isRenewalPlanChange={isRenewalPlanChange}
              close={close}
              isLoading={isCalculatingPayPlan}
              isFormIncomplete={isFormIncomplete()}
              onContinue={createHandleContinue({
                installmentInterval: values.installmentInterval,
                daysOfWeek: values.daysOfWeek,
                daysOfMonth: values.daysOfMonth?.toString(),
                feeStrategy: values.selectedStrategy,
              })}
            />
          </>
        );
      }}
    </Formik>
  );
};
